// frontend/src/lib/planMeta.js
//
// Single bridge between the *machine* plan facts (the capitalised `plan.name`
// returned by GET /api/orgs/me/usage) and the *marketing* plan facts in
// `marketing/data/pricing.js`. Everything plan-aware in the private app — the
// PlanBadge, usage nudges, lock chips / FeatureGate — reads through here so the
// numbers always match the public pricing page (PRIVATE-AREA-PLAN PA1-T6).

import { PLANS, COMPARISON } from "../marketing/data/pricing";

/** Tier order, lowest → highest. Matches the pricing.js plan ids. */
export const TIER_ORDER = ["free", "starter", "pro", "business"];

/**
 * Per-tier accent used by the PlanBadge so the plan is glanceable:
 * Free = grey, Starter = blue, Pro = brand, Business = accent (green).
 * Classes are spelled out (not interpolated) so Tailwind keeps them.
 */
export const TIER_TONES = {
  free: "bg-gray-100 text-gray-700 ring-gray-200",
  starter: "bg-blue-50 text-blue-700 ring-blue-200",
  pro: "bg-brand-50 text-brand-700 ring-brand-200",
  business: "bg-accent-50 text-accent-700 ring-accent-100",
};

/** Normalise a (possibly capitalised / spaced) plan name → a pricing.js id. */
export function planId(name) {
  return String(name || "free").trim().toLowerCase();
}

/** The pricing.js PLANS entry for a plan id (falls back to Free). */
export function planById(id) {
  return PLANS.find((p) => p.id === id) || PLANS[0];
}

/** Zero-based tier index for ordering / comparison (Free = 0). */
export function tierIndex(id) {
  const i = TIER_ORDER.indexOf(id);
  return i < 0 ? 0 : i;
}

/** The next tier up as a PLANS entry, or null when already at the top. */
export function nextTier(id) {
  const i = TIER_ORDER.indexOf(id);
  if (i < 0 || i >= TIER_ORDER.length - 1) return null;
  return planById(TIER_ORDER[i + 1]);
}

/** Raw COMPARISON cell value for a metric label on a plan id, or null. */
export function metric(id, label) {
  const row = COMPARISON.find((r) => r.label === label);
  return row ? row[id] ?? null : null;
}

/** Parse a comparison cell like "50,000" or "$59" into a Number (NaN if none). */
export function metricNumber(id, label) {
  const raw = metric(id, label);
  if (raw == null) return NaN;
  return Number(String(raw).replace(/[^0-9.]/g, ""));
}

/**
 * Is a boolean comparison-row feature available on a plan id? Only `true`/`false`
 * rows are treated as gateable features (numeric/string rows are limits, not gates).
 */
export function hasFeature(id, label) {
  const row = COMPARISON.find((r) => r.label === label);
  return row ? row[id] === true : false;
}

/**
 * The lowest tier that unlocks a boolean feature, as a PLANS entry (or null).
 * Used by lock chips to label "Pro" vs "Business".
 */
export function tierForFeature(label) {
  const row = COMPARISON.find((r) => r.label === label);
  if (!row) return null;
  for (const id of TIER_ORDER) {
    if (row[id] === true) return planById(id);
  }
  return null;
}

/**
 * Resolve everything plan-aware for a live plan name in one call.
 *
 * @param {string} name capitalised plan name from getUsage().plan.name
 * @returns {{ id, plan, index, isTop, next }}
 */
export function planMeta(name) {
  const id = planId(name);
  return {
    id,
    plan: planById(id),
    index: tierIndex(id),
    isTop: tierIndex(id) >= TIER_ORDER.length - 1,
    next: nextTier(id),
  };
}
